import { core } from '@map-my-money/engine';
import { scaleBand, scaleLinear } from 'd3-scale';
import { area, line } from 'd3-shape';
import { useMemo, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { t } from '../../i18n/index.ts';
import { monthYear } from '../../lib/format.ts';
import { useWidth } from '../../lib/hooks.ts';
import { indexAt, type MapFlow, type MapMilestone, type MapMonth } from '../../lib/sharedMap.ts';
import { clamp, moneyTicks, nearestIndex } from './chartUtils.ts';

export const SERIES_COLOR = {
  netWorth: 'var(--s-nw)',
  shared: 'var(--s-shared)',
  cash: 'var(--s-cash)',
  investments: 'var(--s-inv)',
  home: 'var(--s-home)',
  owed: 'var(--s-owed)',
} as const;

export const FLOW_COLOR = {
  income: 'var(--s-cash)',
  spending: 'var(--amber)',
  debt: 'var(--s-owed)',
  invested: 'var(--s-inv)',
  left: 'var(--s-left)',
} as const;

type Layer = { key: 'home' | 'investments' | 'cash' | 'owed'; lo: (m: MapMonth) => number; hi: (m: MapMonth) => number };
const pos = (v: number) => Math.max(0, v);
const LAYERS: readonly Layer[] = [
  { key: 'home', lo: () => 0, hi: (m) => pos(m.home) },
  { key: 'investments', lo: (m) => pos(m.home), hi: (m) => pos(m.home) + pos(m.investments) },
  { key: 'cash', lo: (m) => pos(m.home) + pos(m.investments), hi: (m) => pos(m.home) + pos(m.investments) + pos(m.cash) },
  { key: 'owed', lo: () => 0, hi: (m) => -m.owed },
];

export interface NetWorthMapChartProps {
  readonly months: readonly MapMonth[];
  /** The shared map's net worth, drawn dashed once the visitor has changed something. */
  readonly shared: readonly MapMonth[] | null;
  readonly milestones: readonly MapMilestone[];
  readonly index: number;
  readonly onIndex: (index: number) => void;
  readonly height: number;
  readonly axis: (value: number) => string;
  readonly valueText: (index: number) => string;
  readonly compact: boolean;
}

/**
 * Net worth month by month over the stacked split (assets up, what is owed down). The plot is
 * decorative; an HTML slider laid over it is the scrubber for pointer, touch and keyboard.
 */
export function NetWorthMapChart({ months, shared, milestones, index, onIndex, height, axis, valueText, compact }: NetWorthMapChartProps) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [tip, setTip] = useState<string | null>(null);
  const margin = { top: 30, right: compact ? 10 : 16, bottom: 26, left: compact ? 52 : 58 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const layout = useMemo(() => {
    if (months.length < 2 || innerW <= 0) return null;
    let lo = 0;
    let hi = 0;
    for (const [i, m] of months.entries()) {
      lo = Math.min(lo, -m.owed, m.netWorth);
      hi = Math.max(hi, pos(m.home) + pos(m.investments) + pos(m.cash), m.netWorth, shared?.[i]?.netWorth ?? 0);
    }
    const ticks = moneyTicks(lo, hi, compact ? 3 : 4);
    const x = scaleLinear().domain([0, months.length - 1]).range([0, innerW]);
    const y = scaleLinear().domain([ticks.lo, ticks.hi]).range([innerH, 0]);
    const paths = LAYERS.map((layer) => ({
      key: layer.key,
      fill: area<MapMonth>().x((_, i) => x(i)).y0((m) => y(layer.lo(m))).y1((m) => y(layer.hi(m)))(months as MapMonth[]) ?? '',
      edge: line<MapMonth>().x((_, i) => x(i)).y((m) => y(layer.hi(m)))(months as MapMonth[]) ?? '',
    }));
    const nw = line<MapMonth>().x((_, i) => x(i)).y((m) => y(m.netWorth))(months as MapMonth[]) ?? '';
    const sharedPath = shared ? line<MapMonth>().x((_, i) => x(i)).y((m) => y(m.netWorth))(shared.slice(0, months.length) as MapMonth[]) ?? '' : null;
    // A December month end is where the next year begins.
    const years = months.flatMap((m, i) => (core.toYmd(m.date).month === 12 ? [{ i, year: core.toYmd(m.date).year + 1 }] : []));
    const fit = Math.max(1, Math.floor(innerW / 52));
    const every = [1, 2, 4, 5, 10].find((n) => Math.ceil(years.length / n) <= fit) ?? 10;
    const yearTicks = years.filter((y) => y.year % every === 0);
    const xs = months.map((_, i) => x(i));
    return { x, y, ticks: ticks.ticks, paths, nw, sharedPath, yearTicks, xs };
  }, [months, shared, innerW, innerH, compact]);

  const pins = useMemo(() => {
    if (!layout) return [];
    const placed: { m: MapMilestone; i: number; left: number; top: number }[] = [];
    for (const m of milestones) {
      const i = indexAt({ months }, m.date);
      const left = margin.left + layout.x(i);
      let top = margin.top + layout.y(months[i]!.netWorth);
      // Pins close together stack upwards instead of covering each other.
      while (placed.some((p) => Math.abs(p.left - left) < 20 && Math.abs(p.top - top) < 24)) top -= 24;
      placed.push({ m, i, left, top });
    }
    return placed;
  }, [layout, milestones, months, margin.left, margin.top]);

  const locate = (e: PointerEvent<HTMLDivElement>) => {
    if (!layout) return;
    const rect = e.currentTarget.getBoundingClientRect();
    onIndex(nearestIndex(layout.xs, e.clientX - rect.left));
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const last = months.length - 1;
    const step = e.shiftKey || e.key === 'PageUp' || e.key === 'PageDown' ? 12 : 1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'PageUp') onIndex(Math.min(last, index + step));
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown' || e.key === 'PageDown') onIndex(Math.max(0, index - step));
    else if (e.key === 'Home') onIndex(0);
    else if (e.key === 'End') onIndex(last);
    else return;
    e.preventDefault();
  };

  const active = months[index];
  const tipPin = pins.find((p) => p.m.key === tip);
  return (
    <div ref={ref} className="smap-chart" style={{ height }}>
      {layout && active && (
        <>
          <svg width={width} height={height} aria-hidden="true" className="smap-chart__svg">
            <g transform={`translate(${margin.left},${margin.top})`}>
              {layout.ticks.map((tick) => (
                <g key={tick}>
                  <line x1={0} x2={innerW} y1={layout.y(tick)} y2={layout.y(tick)} className={tick === 0 ? 'chart__gridline chart__gridline--base' : 'chart__gridline'} />
                  <text x={-10} y={layout.y(tick)} dy="0.34em" textAnchor="end" className="chart__axis-label">{axis(tick)}</text>
                </g>
              ))}
              {layout.yearTicks.map((tick) => {
                const tx = layout.x(tick.i);
                const anchor = tx > innerW - 18 ? 'end' : tx < 18 ? 'start' : 'middle';
                return <text key={tick.year} x={tx} y={innerH + 18} textAnchor={anchor} className="chart__axis-label">{tick.year}</text>;
              })}
              <g className="smap-chart__layers">
                {layout.paths.map((p) => (
                  <g key={p.key}>
                    <path d={p.fill} fill={SERIES_COLOR[p.key]} fillOpacity={0.2} />
                    <path d={p.edge} fill="none" stroke={SERIES_COLOR[p.key]} strokeOpacity={0.55} strokeWidth={1} />
                  </g>
                ))}
              </g>
              {layout.sharedPath && (
                <path d={layout.sharedPath} fill="none" stroke={SERIES_COLOR.shared} strokeWidth={2} strokeDasharray="5 5" strokeLinecap="round" />
              )}
              <path d={layout.nw} fill="none" stroke={SERIES_COLOR.netWorth} strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round" className="chart__reveal" />
              <line x1={layout.x(index)} x2={layout.x(index)} y1={-8} y2={innerH} className="chart__crosshair-line" />
              {shared && shared[index] && (
                <circle cx={layout.x(index)} cy={layout.y(shared[index]!.netWorth)} r={4} fill="var(--card)" stroke={SERIES_COLOR.shared} strokeWidth={2} />
              )}
              <circle cx={layout.x(index)} cy={layout.y(active.netWorth)} r={5.5} fill={SERIES_COLOR.netWorth} stroke="var(--card)" strokeWidth={2.5} />
            </g>
          </svg>
          <div
            className="chart__overlay smap-chart__scrub"
            role="slider"
            tabIndex={0}
            aria-label={t('smap.scrubber')}
            aria-valuemin={0}
            aria-valuemax={months.length - 1}
            aria-valuenow={index}
            aria-valuetext={valueText(index)}
            style={{ left: margin.left, top: margin.top - 8, width: innerW, height: innerH + 8 }}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture?.(e.pointerId);
              locate(e);
            }}
            onPointerMove={(e) => {
              if (e.pointerType === 'mouse' || e.buttons > 0) locate(e);
            }}
            onKeyDown={onKeyDown}
          />
          {pins.map((p) => (
            <button
              key={p.m.key}
              type="button"
              className="smap-pin"
              style={{ left: p.left, top: p.top }}
              aria-label={t('smap.pin', { label: p.m.label, date: monthYear(p.m.date) })}
              onClick={() => onIndex(p.i)}
              onPointerEnter={() => setTip(p.m.key)}
              onPointerLeave={() => setTip(null)}
              onFocus={() => setTip(p.m.key)}
              onBlur={() => setTip(null)}
            >
              <svg width="18" height="21" viewBox="0 0 26 30" aria-hidden="true">
                <path d="M13 29C13 29 24 18.5 24 11A11 11 0 0 0 2 11C2 18.5 13 29 13 29Z" fill="var(--mint)" stroke="var(--card)" strokeWidth="2" />
                <circle cx="13" cy="11" r="4.5" fill="#000" />
              </svg>
            </button>
          ))}
          {tipPin && (
            <div
              className="chart-tooltip smap-pin-tip"
              style={{
                left: clamp(tipPin.left - 90, 0, Math.max(0, width - 180)),
                top: Math.max(0, tipPin.top - 84),
              }}
            >
              <span className="chart-tooltip__date">{monthYear(tipPin.m.date)}</span>
              <span className="smap-pin-tip__label">{tipPin.m.label}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export interface FlowChartProps {
  readonly flows: readonly MapFlow[];
  readonly height: number;
  readonly axis: (value: number) => string;
  readonly value: (value: number) => string;
  readonly label: string;
}

const flowParts = (f: MapFlow) => [
  { key: 'spending' as const, value: pos(f.spending) },
  ...(f.debt === null ? [] : [{ key: 'debt' as const, value: pos(f.debt) }]),
  { key: 'invested' as const, value: pos(f.invested) },
];
export const leftOver = (f: MapFlow) => f.income - f.spending - (f.debt ?? 0) - f.invested;

/** Twelve months: what goes out stacked up from zero, income as a mint cap, what's left as a dot. */
export function MonthlyFlowChart({ flows, height, axis, value, label }: FlowChartProps) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const margin = { top: 10, right: 6, bottom: 24, left: 44 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);
  const layout = useMemo(() => {
    if (flows.length === 0 || innerW <= 0) return null;
    const hi = Math.max(...flows.map((f) => Math.max(f.income, flowParts(f).reduce((s, p) => s + p.value, 0))));
    const lo = Math.min(0, ...flows.map(leftOver));
    const ticks = moneyTicks(lo, hi, 3);
    const x = scaleBand<number>().domain(flows.map((_, i) => i)).range([0, innerW]).paddingInner(0.32).paddingOuter(0.12);
    const y = scaleLinear().domain([ticks.lo, ticks.hi]).range([innerH, 0]);
    const labelEvery = innerW / flows.length < 34 ? 3 : 2;
    return { x, y, ticks: ticks.ticks, labelEvery };
  }, [flows, innerW, innerH]);

  const current = active === null ? null : flows[active];
  return (
    <figure className="chart smap-flow" aria-label={label}>
      <div ref={ref} className="chart__frame" style={{ height }} onPointerLeave={() => setActive(null)}>
        {layout && (
          <svg width={width} height={height} aria-hidden="true">
            <g transform={`translate(${margin.left},${margin.top})`}>
              {layout.ticks.map((tick) => (
                <g key={tick}>
                  <line x1={0} x2={innerW} y1={layout.y(tick)} y2={layout.y(tick)} className={tick === 0 ? 'chart__gridline chart__gridline--base' : 'chart__gridline'} />
                  <text x={-8} y={layout.y(tick)} dy="0.34em" textAnchor="end" className="chart__axis-label">{axis(tick)}</text>
                </g>
              ))}
              {flows.map((f, i) => {
                const bx = layout.x(i) ?? 0;
                const bw = layout.x.bandwidth();
                let base = 0;
                const { month } = core.toYmd(f.date);
                const showLabel = i % layout.labelEvery === 0;
                return (
                  <g key={f.date} opacity={active === null || active === i ? 1 : 0.5}>
                    {flowParts(f).map((p) => {
                      const y0 = layout.y(base);
                      base += p.value;
                      const y1 = layout.y(base);
                      return <rect key={p.key} x={bx} y={y1 + 1} width={bw} height={Math.max(0, y0 - y1 - 1)} rx={2} fill={FLOW_COLOR[p.key]} fillOpacity={0.85} />;
                    })}
                    <line x1={bx - 2} x2={bx + bw + 2} y1={layout.y(f.income)} y2={layout.y(f.income)} stroke={FLOW_COLOR.income} strokeWidth={2.5} strokeLinecap="round" />
                    <circle cx={bx + bw / 2} cy={layout.y(leftOver(f))} r={3.5} fill="var(--card)" stroke={FLOW_COLOR.left} strokeWidth={2} />
                    {showLabel && (
                      <text x={bx + bw / 2} y={innerH + 17} textAnchor="middle" className="chart__axis-label">
                        {month === 1 || i === 0 ? `${t(`month.short.${month}` as 'month.short.1')} ’${String(core.toYmd(f.date).year).slice(2)}` : t(`month.short.${month}` as 'month.short.1')}
                      </text>
                    )}
                    <rect
                      x={bx - (layout.x.step() - bw) / 2}
                      y={0}
                      width={layout.x.step()}
                      height={innerH}
                      fill="transparent"
                      onPointerEnter={() => setActive(i)}
                      onPointerDown={() => setActive(i)}
                    />
                  </g>
                );
              })}
            </g>
          </svg>
        )}
        {layout && current && active !== null && (
          <div
            className="chart-tooltip smap-flow__tip"
            style={{
              left: clamp(margin.left + (layout.x(active) ?? 0) + layout.x.bandwidth() / 2 - 85, 0, Math.max(0, width - 170)),
              top: 0,
            }}
          >
            <span className="chart-tooltip__date">{monthYear(current.date)}</span>
            {(['income', 'spending', 'debt', 'invested'] as const).map((k) => {
              const v = current[k];
              if (v === null) return null;
              return (
                <span key={k} className="chart-tooltip__row">
                  <i style={{ background: FLOW_COLOR[k] }} />
                  {k === 'spending' && current.debt === null ? t('smap.flow.spendingAll') : t(`smap.flow.${k}`)}
                  <b>{value(v)}</b>
                </span>
              );
            })}
            <span className="chart-tooltip__row"><i style={{ background: FLOW_COLOR.left, borderRadius: '50%' }} />{t('smap.flow.left')}<b>{value(leftOver(current))}</b></span>
          </div>
        )}
      </div>
    </figure>
  );
}

